import { useState, useRef, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, X, Loader2, Check } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { fetchPlanVersions } from '@/api/planDetail'
import { useMoveLines } from '@/hooks/usePlanDetail'
import { usePlanSearch } from '@/hooks/usePlanSearch'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import { cn } from '@/utils/cn'
import type { SearchablePlan } from '@/api/planSearch'
import type { PlanDetailItem } from '@/types'

interface MoveToPlanModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  versionId: string
  selectedItems: PlanDetailItem[]
  onSuccess: () => void
}

export function MoveToPlanModal({
  open,
  onOpenChange,
  planId,
  versionId,
  selectedItems,
  onSuccess,
}: MoveToPlanModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<SearchablePlan | null>(null)
  const [selectedVersionId, setSelectedVersionId] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const listRef = useRef<HTMLDivElement>(null)

  const moveLinesMutation = useMoveLines(planId)

  // Plan search (inline, not using PlanSearcher component)
  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    usePlanSearch({ query: searchQuery })
  const plans = data?.pages.flatMap(page => page.plans) ?? []

  // Version fetch for selected plan
  const { data: versions, isLoading: versionsLoading } = useQuery({
    queryKey: queryKeys.versions.byPlanId(selectedPlan?.id ?? ''),
    queryFn: () => fetchPlanVersions(selectedPlan!.id),
    enabled: !!selectedPlan,
  })

  const handleScroll = useCallback(() => {
    if (!listRef.current || !hasNextPage || isFetchingNextPage) return
    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const handleSelectPlan = (plan: SearchablePlan) => {
    setSelectedPlan(plan)
    setSelectedVersionId('')
    setSearchQuery('')
  }

  const handleClearPlan = () => {
    setSelectedPlan(null)
    setSelectedVersionId('')
  }

  const handleConfirm = () => {
    if (!selectedPlan || !selectedVersionId) return

    moveLinesMutation.mutate(
      {
        versionId,
        lineIds: selectedItems.map(item => item.id),
        targetVersionId: selectedVersionId,
        targetPlanId: selectedPlan.id,
      },
      {
        onSuccess: () => {
          toast.success(
            `${selectedItems.length} ${selectedItems.length === 1 ? 'line' : 'lines'} moved successfully`
          )
          handleReset()
          onOpenChange(false)
          onSuccess()
        },
        onError: (error: Error) => {
          toast.error(`Failed to move lines: ${error.message}`)
        },
      }
    )
  }

  const handleReset = () => {
    setSelectedPlan(null)
    setSelectedVersionId('')
    setSearchQuery('')
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) handleReset()
    onOpenChange(nextOpen)
  }

  const canConfirm = !!selectedPlan && !!selectedVersionId

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Move to Plan</DialogTitle>
          <DialogDescription>
            Move {selectedItems.length}{' '}
            {selectedItems.length === 1 ? 'line' : 'lines'} to another plan
            version.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 min-h-0">
          {/* Plan selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#1c1c1c]">
              Target Plan
            </label>

            {selectedPlan ? (
              <div className="flex items-center gap-2 rounded-lg border border-primary bg-primary/5 px-3 py-2.5">
                <Check className="h-4 w-4 text-primary flex-shrink-0" />
                <span className="flex-1 text-sm font-medium truncate">
                  {selectedPlan.name}
                </span>
                <button
                  type="button"
                  onClick={handleClearPlan}
                  className="cursor-pointer text-[#556374] hover:text-[#1c1c1c] flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                {/* Inline search input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#556374]" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search for a plan..."
                    autoFocus
                    className={cn(
                      'w-full h-10 pl-9 pr-3 text-sm',
                      'border border-[#D6DAE1] rounded-lg',
                      'placeholder:text-[#9CA3AF]',
                      'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
                    )}
                  />
                  {isLoading && !isFetchingNextPage && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#556374] animate-spin" />
                  )}
                </div>

                {/* Inline results list */}
                <div
                  ref={listRef}
                  onScroll={handleScroll}
                  className="border border-[#D6DAE1] rounded-lg max-h-[240px] overflow-y-auto"
                >
                  {isLoading && !isFetchingNextPage ? (
                    <div className="px-4 py-6 text-center text-[#556374]">
                      <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                      <span className="text-sm">Searching...</span>
                    </div>
                  ) : plans.length > 0 ? (
                    <div className="p-1">
                      {plans.map(plan => (
                        <button
                          key={plan.id}
                          type="button"
                          onClick={() => handleSelectPlan(plan)}
                          className={cn(
                            'w-full text-left px-3 py-2.5 rounded-md',
                            'hover:bg-[#f1f5f9] cursor-pointer',
                            'transition-colors duration-150'
                          )}
                        >
                          <p className="text-sm font-medium text-[#1C1C1C] break-all">
                            {plan.name}
                          </p>
                        </button>
                      ))}
                      {isFetchingNextPage && (
                        <div className="py-3 text-center">
                          <Loader2 className="h-4 w-4 animate-spin mx-auto text-[#556374]" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="px-4 py-6 text-center text-[#556374]">
                      <span className="text-sm">
                        {searchQuery
                          ? `No plans found for "${searchQuery}"`
                          : 'No plans available'}
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Version selection - shown as radio-style cards */}
          {selectedPlan && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#1c1c1c]">
                Target Version
              </label>
              {versionsLoading ? (
                <div className="flex items-center gap-2 py-3 text-sm text-[#556374]">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading versions...
                </div>
              ) : versions && versions.length > 0 ? (
                <div className="border border-[#D6DAE1] rounded-lg divide-y divide-[#D6DAE1] max-h-[200px] overflow-y-auto">
                  {versions.map(v => {
                    const isSelected = selectedVersionId === v.id
                    const label = v.alias || `Version ${v.version_number}`

                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => setSelectedVersionId(v.id)}
                        className={cn(
                          'w-full text-left px-3 py-2.5 cursor-pointer',
                          'transition-colors duration-150',
                          'first:rounded-t-lg last:rounded-b-lg',
                          isSelected ? 'bg-primary/5' : 'hover:bg-[#f1f5f9]'
                        )}
                      >
                        <div className="flex items-center gap-2">
                          {/* Radio indicator */}
                          <div
                            className={cn(
                              'h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                              isSelected ? 'border-primary' : 'border-[#D6DAE1]'
                            )}
                          >
                            {isSelected && (
                              <div className="h-2 w-2 rounded-full bg-primary" />
                            )}
                          </div>

                          <span className="text-sm font-medium text-[#1C1C1C] truncate">
                            {label}
                          </span>

                          {v.is_active && (
                            <span className="text-xs font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded flex-shrink-0">
                              active
                            </span>
                          )}
                        </div>

                        {v.notes && (
                          <p className="text-xs text-[#556374] mt-1 ml-6 line-clamp-2">
                            {v.notes}
                          </p>
                        )}
                      </button>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-[#556374] py-2">
                  No versions available for this plan.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={moveLinesMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm || moveLinesMutation.isPending}
          >
            {moveLinesMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Moving...
              </>
            ) : (
              'Move'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
